import type { Phase } from "api/types/aba";
import type { SessionMetricPoint } from "api/types/charts";
import type { ClinicalPhaseZone } from "../Targets/TargetChart";

/** Title-cases a phase id for the zone label, e.g. "generalization" → "Generalization". */
function phaseLabel(phase: Phase): string {
  return phase.charAt(0).toUpperCase() + phase.slice(1);
}

/**
 * Collapses a session-metrics series into clinical phase background bands.
 *
 * The chart plots one point per session in array order, so a zone's
 * `startIndex`/`endIndex` are positions in `metrics` (parallel to the chart's
 * labels), not session ids. Consecutive points sharing a phase merge into one
 * zone; a phase that recurs after a different phase yields a second zone.
 *
 * Points without a `phase` (legacy/fixture data) don't open a zone and break
 * the current run, so a gap can't silently absorb into a neighbouring band.
 *
 * @param metrics - Session metric points, oldest first (as fed to the chart).
 * @returns Phase zones in chart order; empty when nothing carries a phase.
 */
export function derivePhaseZones(metrics: SessionMetricPoint[]): ClinicalPhaseZone[] {
  const zones: ClinicalPhaseZone[] = [];

  metrics.forEach((point, index) => {
    const phase = point.phase;
    if (!phase) return;

    const current = zones[zones.length - 1];
    if (current && current.phase === phase && current.endIndex === index - 1) {
      current.endIndex = index;
    } else {
      zones.push({ label: phaseLabel(phase), phase, startIndex: index, endIndex: index });
    }
  });

  return zones;
}
